import { useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, errorText, useFetch } from "../api";
import { Alerts, Badge, ConfirmDialog, EmptyRow, Facts, Loading, Page, Pager, ReasonDialog, Tabs, UserLink } from "../components/ui";
import { label, money, PAGE_SIZE, percent, qs, setQuery, text, when } from "../format";

function WithdrawalActions({ id, status, pay, onDone }) {
  const [mode, setMode] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState({ message: "", errors: {} });
  const failed = status === "failed" || status === "processing";

  function close() {
    if (busy) return;
    setMode("");
    setProblem({ message: "", errors: {} });
  }

  async function approve() {
    setBusy(true);
    setProblem({ message: "", errors: {} });
    try {
      const payload = await api("/withdrawals/" + id + (failed ? "/retry" : "/approve"), { method: "POST" });
      setMode("");
      onDone(payload.message || "Withdrawal sent");
    } catch (err) {
      setProblem({ message: errorText(err), errors: err.errors || {} });
    } finally {
      setBusy(false);
    }
  }

  async function reject(reason) {
    setBusy(true);
    setProblem({ message: "", errors: {} });
    try {
      await api("/withdrawals/" + id + "/reject", { method: "POST", body: { reason } });
      setMode("");
      onDone("Withdrawal rejected");
    } catch (err) {
      setProblem({ message: errorText(err), errors: err.errors || {} });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="row-actions" onClick={(event) => event.stopPropagation()}>
      {failed ? (
        <button type="button" className="btn small" onClick={() => setMode("approve")}>Retry</button>
      ) : (
        <button type="button" className="btn small" onClick={() => setMode("approve")}>Approve</button>
      )}
      <button type="button" className="btn danger small" onClick={() => setMode("reject")}>Reject</button>
      {mode === "approve" ? (
        <ConfirmDialog
          title={failed ? "Retry withdrawal" : "Approve withdrawal"}
          text={"This sends " + (pay == null ? "the pay amount" : money(pay) + " USDT") + " on BEP20 from the admin payout wallet. The fee stays on record and is not sent. The full requested amount stays locked until the transfer succeeds. The member is emailed only after that send succeeds."}
          error={problem.message}
          busy={busy}
          confirmLabel={failed ? "Retry" : "Send"}
          onClose={close}
          onConfirm={approve}
        />
      ) : null}
      {mode === "reject" ? (
        <ReasonDialog
          title="Reject withdrawal"
          label="Reason"
          hint="The full requested amount returns to the same wallet. The fee is not sent. The member is emailed this reason."
          error={problem.errors.reason || problem.message}
          busy={busy}
          max={300}
          onClose={close}
          onSubmit={reject}
        />
      ) : null}
    </div>
  );
}

export default function Withdrawals() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const page = Math.max(1, Number(params.get("page")) || 1);
  const status = params.get("status") || "pending";
  const list = useFetch("/withdrawals" + qs({ page, limit: PAGE_SIZE, status }));
  const rows = Array.isArray(list.data?.data) ? list.data.data : [];
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);

  async function approveAll() {
    setBusy(true);
    setNotice("");
    try {
      const payload = await api("/withdrawals/approve-all", { method: "POST" });
      const counts = payload.data || {};
      setNotice(payload.message + (counts.total ? ` · sent ${counts.approved || 0}, failed ${counts.failed || 0}` : ""));
      list.reload();
    } catch (err) {
      setNotice(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Page title="Withdrawals" text={<>Pay is what gets sent. A request of 10 at 10% fee shows Requested 10, Fee 1, and Pay 9. Approve sends Pay in USDT on BEP20 from the <Link to="/admin/payout-wallet">payout wallet</Link>. The fee is only a record and is not sent. The full requested amount stays locked until that send succeeds. Reject returns the full requested amount.</>}>
      <Alerts error={list.error} notice={notice} />
      {status === "pending" ? (
        <div className="block-gap">
          <button type="button" className="btn" disabled={busy || !rows.length} onClick={approveAll}>
            {busy ? "Sending…" : "Approve all pending"}
          </button>
        </div>
      ) : null}
      <Tabs
        value={status}
        onChange={(value) => {
          setNotice("");
          setQuery(params, setParams, { status: value, page: "" });
        }}
        options={[
          { value: "pending", label: "Pending" },
          { value: "processing", label: "Processing" },
          { value: "failed", label: "Failed" },
          { value: "approved", label: "Approved" },
          { value: "rejected", label: "Rejected" },
          { value: "cancelled", label: "Cancelled" },
        ]}
      />
      {list.loading ? <Loading /> : (
        <div className="table-wrap">
          <table className="wide">
            <thead>
              <tr>
                <th>Member</th><th className="num">Requested</th><th className="num">Fee %</th><th className="num">Fee</th><th className="num">Pay</th>
                <th>Network</th><th>Address</th><th>Status</th><th></th>
              </tr>
            </thead>
            <tbody>
              {rows.length ? rows.map((row) => (
                <tr key={row.id} className="click" onClick={() => navigate("/admin/withdrawals/" + row.id)}>
                  <td onClick={(event) => event.stopPropagation()}><UserLink user={row.user} userId={row.userId} /></td>
                  <td className="num">{money(row.amount)}</td>
                  <td className="num">{percent(row.feePercent)}</td>
                  <td className="num">{money(row.fee)}</td>
                  <td className="num net">{money(row.netAmount)}</td>
                  <td>{label(row.network)}</td>
                  <td className="mono">{row.address}</td>
                  <td><Badge value={row.status} /></td>
                  <td>
                    {row.status === "pending" || row.status === "failed" || row.status === "processing"
                      ? <WithdrawalActions id={row.id} status={row.status} pay={row.netAmount} onDone={(message) => { setNotice(message); list.reload(); }} />
                      : text(row.transactionHash || row.reason)}
                  </td>
                </tr>
              )) : <EmptyRow cols={9} text="No withdrawals" />}
            </tbody>
          </table>
        </div>
      )}
      {list.data ? (
        <Pager page={page} limit={PAGE_SIZE} total={list.data.total} onPage={(next) => setQuery(params, setParams, { page: next <= 1 ? "" : next })} />
      ) : null}
    </Page>
  );
}

export function WithdrawalDetail() {
  const { id } = useParams();
  const { data, loading, error, reload } = useFetch("/withdrawals/" + id);
  const row = data?.data;
  const [notice, setNotice] = useState("");
  const [copied, setCopied] = useState(false);

  function copyAddress() {
    if (!row || !navigator.clipboard) return;
    navigator.clipboard.writeText(row.address).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    });
  }

  return (
    <Page title="Withdrawal">
      <Link className="back" to="/admin/withdrawals">Back to withdrawals</Link>
      <Alerts error={error} notice={notice} />
      {loading ? <Loading /> : null}
      {row ? (
        <>
          <div className="pay-box">
            <span>Pay this amount</span>
            <strong>{money(row.netAmount)}</strong>
            <p>{label(row.network)} · <span className="mono">{row.address}</span></p>
            <button type="button" className="btn ghost small" onClick={copyAddress}>{copied ? "Copied" : "Copy address"}</button>
          </div>
          <div className="panel">
            <Facts items={[
              { label: "Requested", value: money(row.amount) },
              { label: "Fee percent", value: percent(row.feePercent) },
              { label: "Fee on record", value: money(row.fee) },
              { label: "Status", value: <Badge value={row.status} /> },
              { label: "Address label", value: text(row.label) },
              { label: "Member", value: <UserLink user={row.user} userId={row.userId} /> },
              { label: "Requested", value: when(row.createdAt) },
              { label: "Reviewed", value: when(row.reviewedAt) },
              { label: "Reason", value: text(row.reason) },
              { label: "Transaction", value: text(row.transactionHash) },
            ]} />
            {row.status === "pending" || row.status === "failed" || row.status === "processing" ? (
              <div className="block-gap">
                <WithdrawalActions id={row.id} status={row.status} pay={row.netAmount} onDone={(message) => { setNotice(message); reload(); }} />
              </div>
            ) : null}
          </div>
        </>
      ) : null}
    </Page>
  );
}
